// Contexte capturé automatiquement au dépôt : page, viewport, navigateur,
// erreurs console récentes (masquées), capture d'écran à la demande. Repris
// de useConsoleErrorBuffer.ts et screenshot.ts (Faast), sans React.
import type { ConsoleError } from '@feedbaacks/sdk'

const MAX_AGE = 60_000
const MAX_ENTRIES = 50
const MAX_ENTRY_CHARS = 1000

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
const BEARER_RE = /(?:Bearer\s+)?[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}/g
const AUTH_HEADER_RE = /Bearer\s+[A-Za-z0-9._-]+/g
const KEY_ASSIGN_RE = /(api[_-]?key|token|secret|password)\s*[=:]\s*['"]?[A-Za-z0-9._-]{8,}['"]?/gi

/** Masque e-mails, jetons et secrets avant que rien ne quitte la page. */
export function maskSensitive(input: string): string {
  return input
    .replace(BEARER_RE, '[REDACTED_TOKEN]')
    .replace(AUTH_HEADER_RE, 'Bearer [REDACTED]')
    .replace(KEY_ASSIGN_RE, (_, key: string) => `${key}=[REDACTED]`)
    .replace(EMAIL_RE, '[REDACTED_EMAIL]')
}

/** JSON.stringify qui survit aux cycles, Error, fonctions et BigInt. */
export function safeStringify(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}${value.stack ? `\n${value.stack}` : ''}`
  if (typeof value === 'string') return value
  if (typeof value === 'function') return `[Function ${value.name || 'anonymous'}]`
  if (typeof value === 'bigint') return `${value.toString()}n`
  if (value === undefined) return 'undefined'
  const seen = new WeakSet<object>()
  try {
    return (
      JSON.stringify(value, (_k, v: unknown) => {
        if (typeof v === 'bigint') return `${v.toString()}n`
        if (typeof v === 'function') return `[Function ${(v as { name?: string }).name || 'anonymous'}]`
        if (typeof v === 'object' && v !== null) {
          if (seen.has(v)) return '[Circular]'
          seen.add(v)
        }
        return v
      }) ?? String(value)
    )
  } catch {
    return '[Unserialisable]'
  }
}

interface RawEntry {
  ts: number
  args: unknown[]
}

let buffer: RawEntry[] = []
let installed = 0
let original: typeof console.error | null = null
let wrapper: typeof console.error | null = null

/** Installe le tampon console.error (compté : plusieurs widgets, un seul
 *  tampon). La fonction rendue le retire quand plus personne ne l'utilise. */
export function installConsoleBuffer(): () => void {
  if (typeof console === 'undefined') return () => {}
  if (installed++ === 0) {
    original = console.error
    wrapper = (...args: unknown[]) => {
      const now = Date.now()
      buffer.push({ ts: now, args })
      buffer = buffer.filter((e) => e.ts >= now - MAX_AGE).slice(-MAX_ENTRIES)
      original?.(...args)
    }
    console.error = wrapper
  }
  return () => {
    if (--installed === 0 && console.error === wrapper && original) console.error = original
  }
}

export function consoleSnapshot(): ConsoleError[] {
  const cutoff = Date.now() - MAX_AGE
  return buffer
    .filter((e) => e.ts >= cutoff)
    .map((e) => {
      const masked = maskSensitive(e.args.map(safeStringify).join(' '))
      return {
        ts: new Date(e.ts).toISOString(),
        message: masked.length > MAX_ENTRY_CHARS ? `${masked.slice(0, MAX_ENTRY_CHARS)}…` : masked,
      }
    })
}

export interface PageSnapshot {
  page_url: string
  viewport: string
  user_agent: string
}

export function pageSnapshot(): PageSnapshot {
  if (typeof window === 'undefined') return { page_url: '', viewport: '', user_agent: '' }
  return {
    page_url: window.location.href,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    user_agent: navigator.userAgent,
  }
}

export function dataUrlToBlob(dataUrl: string): Blob {
  const [header, b64] = dataUrl.split(',')
  const mime = /data:([^;]+);base64/.exec(header)?.[1] ?? 'image/png'
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

// Deux images pour que le masquage soit peint ; un onglet en arrière-plan
// suspend requestAnimationFrame : une minuterie prend alors le relais.
const waitTwoFrames = () =>
  new Promise<void>((r) => {
    const timer = setTimeout(r, 100)
    requestAnimationFrame(() => requestAnimationFrame(() => {
      clearTimeout(timer)
      r()
    }))
  })

/** Délai maximal d'une capture : au-delà, le retour part sans elle. */
export const CAPTURE_TIMEOUT_MS = 10_000

/** Capture la page sans l'interface Feedbaacks (masquée le temps de la
 *  capture, via l'attribut data-feedbaacks-capturing). html-to-image est
 *  chargé à la demande : il ne pèse rien tant qu'on ne capture pas. */
export async function capturePage(target: HTMLElement = document.body, timeoutMs = CAPTURE_TIMEOUT_MS): Promise<Blob> {
  const root = document.documentElement
  root.setAttribute('data-feedbaacks-capturing', '')
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const capture = (async () => {
      await waitTwoFrames()
      const { toPng } = await import('html-to-image')
      return toPng(target, {
        width: window.innerWidth,
        height: Math.min(target.scrollHeight, 4000),
        pixelRatio: 1,
        cacheBust: true,
        filter: (node) => !(node instanceof HTMLElement && node.hasAttribute('data-feedbaacks-host')),
      })
    })()
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('capture trop longue')), timeoutMs)
    })
    return dataUrlToBlob(await Promise.race([capture, timeout]))
  } finally {
    clearTimeout(timer)
    root.removeAttribute('data-feedbaacks-capturing')
  }
}
